import { Box, IconButton, InputBase, CircularProgress, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridClearIcon } from '@mui/x-data-grid';
import { SearchIcon } from 'components/CommonIcons/Icons';
import { useState, useRef, useEffect, useCallback } from 'react';

interface SearchFieldProps {
  value: string;
  type?: string;
  onChange: (val: string) => void;
  onSearch: () => void;
  onClear: () => void;
  searchIcon?: React.ReactNode;
  searchIconSize?: number;
  otherIconsSize?: number;
  isSearching?: boolean;
  isClearingIcon?: boolean;
  width?: number | string | Record<string, number | string>;
  minWidth?: number | string | Record<string, number | string>;
  maxWidth?: number | string;
}

  const placeholderMap: Record<string, string> = {
    alerts: 'Pipeline Early Warning System',
    jobs: 'Pipeline Run Status',
    health: 'Platform Health',
    incidents: 'Incidents',
    'ops pulse' : 'Ops Pulse Dashboard',
    'Service Requests': 'Service Requests',
    'Incidents': 'Incidents',
    'Problem Tickets': 'Problem Tickets',
    'Change Requests': 'Change Requests',
    programs: 'Programs',
    projects: 'Projects',
    resources: 'Resource'
  };

  type SearchHandlerConfig = {
    value: string;
    onChange: (val: string) => void;
    onSearch: () => void;
    onClear: () => void;
  };

  function handleSearchKeyDown(
    e: React.KeyboardEvent,
    config: SearchHandlerConfig
  ): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      config.onSearch();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      if (config.value.trim().length > 0) {
        config.onClear();
      }
    }
  }

  function handleSearchClearOrClose(config: SearchHandlerConfig): void {
    config.onClear();
  }

const SearchField = ({
  value,
  type,
  onChange,
  onSearch,
  onClear,
  searchIcon,
  searchIconSize = 18,
  otherIconsSize = 18,
  isSearching = false,
  isClearingIcon = false,
  width = { xs: "100%", sm: 180, md: 220, lg: 260 },
  minWidth = { xs: 120, sm: 150, md: 190, lg: 240 },
  maxWidth = 300,
}: SearchFieldProps) => {
  const theme = useTheme();
  const p = theme.palette;
  const placeholder = type ? `${placeholderMap[type]} Search....` : 'Search....';
  
  // Apply platform health dimensions
  const customWidth = type === 'health' ? { xs: 120, sm: 150, md: 190, lg: 220 } : width;
  const customMinWidth = type === 'health' ? { xs: 110, sm: 140, md: 180, lg: 210 } : minWidth;
  const customMaxWidth = type === 'health' ? 220 : maxWidth;

  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = value.trim().length > 0 || localExpanded;
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousValueRef = useRef<string>(value);

  // Check if input text is overflowing
  const checkOverflow = useCallback(() => {
    if (inputRef.current) {
      const isOverflow = inputRef.current.scrollWidth > inputRef.current.clientWidth;
      setIsOverflowing(isOverflow);
    }
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (!value.trim()) {
          setLocalExpanded(false);
        }
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isExpanded, value]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded, value]);

  // Check overflow when value changes
  useEffect(() => {
    checkOverflow();
  }, [value, checkOverflow]);

  // Close search box when value is cleared externally
  useEffect(() => {
    if (previousValueRef.current.trim().length > 0 && value.trim().length === 0) {
      setLocalExpanded(false);
    }
    previousValueRef.current = value;
  }, [value]);

  // Check overflow on window resize
  useEffect(() => {
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [checkOverflow]);

  if (!isExpanded) {
    return (
      <Tooltip title={placeholder} placement="bottom" arrow>
        <Box
          ref={containerRef}
          onClick={() => setLocalExpanded(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            {searchIcon ?? <SearchIcon size={searchIconSize} color={p.modal.searchIconColor} />}
          </Box>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: p.white.main,
        borderRadius: '4px',
        px: 1,
        gap: 0.5,
        boxSizing: 'border-box',
        width: customWidth,
        minWidth: customMinWidth,
        maxWidth: customMaxWidth,
        height: 35,
        transition: 'all 0.3s ease',
      }}
    >
      {isSearching ? (
        <CircularProgress 
          size={otherIconsSize} 
          sx={{ 
            color: p.search.background, 
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }} 
        />
      ) : (
        <Box
          onClick={onSearch}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'color 0.2s ease',
            flexShrink: 0,
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          {searchIcon ?? <SearchIcon size={searchIconSize} color={p.search.background} />}
        </Box>
      )}

      <Tooltip
        title={isOverflowing ? value : ''}
        placement="bottom"
        enterDelay={500}
        leaveDelay={200}
        arrow
      >
        <span style={{ flex: 1, overflow: 'hidden', display: 'flex', width: '100%' }}>
          <InputBase
            inputRef={inputRef}
            value={value}
            onChange={(e) => {
              const newValue = e.target.value;
              onChange(newValue);
              
              if (value.trim().length > 0 && newValue.trim().length === 0) {
                onClear();
              }
            }}
            placeholder={placeholder}
            sx={{
              flex: 1,
              width: '100%',
              fontSize: '0.75rem',
              color: p.inputNew.inputText,
              caretColor: p.inputNew.inputText,
              '& input': {
                padding: 0,
                color: p.inputNew.inputText,
                caretColor: p.inputNew.inputText,
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              '& input::placeholder': {
                color: p.inputNew.inputPlaceholder,
                opacity: 0.7,
              },
            }}
            onKeyDown={(e) =>
              handleSearchKeyDown(e as React.KeyboardEvent, {
                value,
                onChange,
                onSearch,
                onClear,
              })
            }
          />
        </span>
      </Tooltip>

      <IconButton
        onClick={() => {
          handleSearchClearOrClose({
            value,
            onChange,
            onSearch,
            onClear,
          });
          setLocalExpanded(false);
        }}
        size="small"
        disabled={isSearching || isClearingIcon}
        sx={{
          p: 0.25,
          flexShrink: 0,
          transition: 'color 0.2s ease',
          '&:hover': {
            color: p.primary.dark || p.primary.main,
          },
        }}
      >
        {isClearingIcon ? (
          <CircularProgress size={otherIconsSize} sx={{ color: p.modal.sectionTitle }} />
        ) : (
          <GridClearIcon
            sx={{
              fontSize: otherIconsSize,
              color: p.modal.searchIconColor,
              transition: 'color 0.2s ease',
            }}
          />
        )}
      </IconButton>
    </Box>
  );
};

export default SearchField;
